import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Margin, usePDF } from "react-to-pdf";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Logo from "../../assets/SelfSync Logo.png";
import { apiEndpoints } from "../../services/apiConfig";

export default function ViewCandidateReport() {
  const [report, setReport] = useState();
  const { toPDF, targetRef } = usePDF({
    filename: `${report?.candidate_info?.first_name || "report"}.pdf`,
    page: { margin: Margin.SMALL, format: "letter", orientation: "portrait" },
  });
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const getReport = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiEndpoints.viewReport(id), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setReport(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching report:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    getReport();
  }, []);



  const handleBack = () => navigate("/report");

  // const traitData = useMemo(() => {
  //     return report?.traits?.map((t) => ({
  //         name: t.trait,
  //         score: t.score,
  //         insight: t.insight,
  //     })) || [];
  // }, [report?.traits]);

  const traitData = useMemo(() => {
    return (
      report?.traits
        ?.map((t) => ({
          name: t.trait,
          score: t.score,
          insight: t.insight,
        }))
        // 🔠 Sort alphabetically by name
        .sort((a, b) => a.name.localeCompare(b.name)) || []
    );
  }, [report?.traits]);

  const finalScore =
    report?.role_fit_analysis?.current_role_fit_score === undefined
      ? report?.current_role_fit_score
      : report?.role_fit_analysis?.current_role_fit_score;

  const gaugeValue = finalScore || 0;

  const finalScoreValue = finalScore || 0;
  const numericValue = parseFloat(finalScoreValue);

  const formattedValue = `${Number.isInteger(numericValue) ? numericValue : numericValue.toFixed(1)
    }/10`;

  const gaugeData = [
    {
      name: "Fit Score",
      value: gaugeValue,
      fill:
        gaugeValue >= 7 ? "#16a34a" : gaugeValue >= 4 ? "#facc15" : "#ef4444",
    },
  ];

  const rawDate = report?.candidate_info?.assessment_date;
  const date = new Date(rawDate);

  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  const time = `${hours}:${minutes} ${ampm}`;
  const formatted = `${day} ${month}, ${year} - ${time}`;

  const formattedDate = useMemo(() => {
    const rawUTC = report?.candidate_info?.assessment_date;
    if (!rawUTC) return "-";

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const safeUTC = rawUTC.endsWith("Z")
      ? rawUTC
      : rawUTC.replace(" ", "T") + "Z";
    const utcDate = new Date(safeUTC);
    const localDate = utcToZonedTime(utcDate, timeZone);
    return format(localDate, "dd MMM yyyy, hh:mm a");
  }, [report?.candidate_info?.assessment_date]);

  console.log("formattedDate", formattedDate);

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gradient-to-br from-indigo-200 to-white">
        <div className="text-center text-indigo-700 text-xl font-semibold animate-pulse">
          Loading candidate insights...
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-white to-indigo-100 min-h-screen flex flex-col items-center p-10 max-md:px-2 max-md:py-2 max-md:mt-2">
      {/* Candidate Info */}
      <div className="w-full max-w-7xl grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
        {[
          {
            title: "Candidate",
            value: report?.candidate_info?.name,
            color: "violet",
          },
          {
            title:
              report?.candidate_info?.job_title === null
                ? "Email"
                : "Job Title",
            value:
              report?.candidate_info?.job_title === null
                ? report?.candidate_info?.email
                : report?.candidate_info?.job_title,
            color: "blue",
          },
          { title: "Assessment Date", value: formattedDate, color: "green" },
        ].map((info, idx) => (
          <div
            key={idx}
            className={`bg-white px-6 py-3 max-md:py-1 rounded-3xl shadow-lg border-t-4 border-${info.color}-500 transition transform hover:scale-[1.02]`}
          >
            <h3 className="text-xs font-medium text-gray-500 uppercase">
              {info.title}
            </h3>
            <p
              className={`text-base font-semibold text-${info.color}-700 mt-2 max-md:mt-0`}
            >
              {info.value || "-"}
            </p>
          </div>
        ))}
      </div>

      {/* Main Report Content */}
      <div
        ref={targetRef}
        className="relative max-w-7xl w-full bg-white shadow-xl rounded-3xl border border-gray-300 p-10 max-md:p-2 print:p-0 print:border-0 print:shadow-none print:rounded-none"
      >
        <div className="flex justify-between items-center border-b pb-2 mb-4">
          <img src={Logo} alt="Logo" className="h-10 max-md:h-8" />

          <div className="relative w-60 h-10">
            <div className="absolute inset-0 flex justify-end items-end pb-1 max-md:pb-2">
              <span className="text-xl font-bold text-indigo-600 max-md:text-base">
                Overall Score : {formattedValue}{" "}
              </span>
            </div>
          </div>
        </div>

        <p className="text-xs my-3 text-center hidden max-md:block text-gray-600">
          Scroll the bar chart horizontally to view all trait records clearly
        </p>

        <div className="mb-8 bg-gradient-to-tr from-white to-indigo-50 pt-4 rounded-2xl border border-gray-300 shadow-lg">
          <h2 className="text-center text-base font-bold text-indigo-700 mb-6">
            Behavioral Traits – Bar Chart
          </h2>
          <div style={{ overflowX: "auto", width: "100%" }}>
            <div style={{ minWidth: "600px" }}>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={traitData}
                  margin={{ top: 20, right: 30, left: -5, bottom: 0 }}
                  barCategoryGap="25%"
                  barGap={15}
                >
                  <CartesianGrid strokeDasharray="3 5" />
                  <XAxis
                    dataKey="name"
                    angle={-30}
                    textAnchor="end"
                    interval={0}
                    height={100}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} />
                  <Tooltip
                    formatter={(value) => parseFloat(value).toFixed(2)}
                  />
                  <Legend />
                  <Bar dataKey="score" fill="url(#colorUv)">
                    <LabelList
                      dataKey="score"
                      position="top"
                      fontSize={12}
                      fontWeight={600}
                      formatter={(value) => value.toFixed(2)}
                    />
                  </Bar>
                  <defs>
                    <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.9} />
                      <stop
                        offset="100%"
                        stopColor="#4ade80"
                        stopOpacity={0.6}
                      />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Traits Grid */}
        <h2 className="text-start text-2xl font-semibold mb-4">
          Behavioral Traits
        </h2>
        <div className="mb-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {traitData.map((trait, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-xl p-5 shadow-lg hover:shadow-xl transition-all"
            >
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-violet-700">
                  {trait.name}
                </h3>
                <span className="bg-violet-600 text-white text-xs px-3 py-1 rounded-full">
                  {trait.score}
                </span>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                {trait.insight}
              </p>
            </div>
          ))}
        </div>

        <h2 className="text-start text-2xl font-bold">Overall Analysis</h2>

        <div
          className={`grid grid-cols-1 ${report?.role_fit_analysis?.current_role_fit_score === undefined
            ? "md:grid-cols-1"
            : "md:grid-cols-2"
            } gap-6 text-gray-700 text-sm mt-4`}
        >
          {/* Role Fit Analysis */}
          <div className="bg-white border border-indigo-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-all">
            <h3 className="text-lg font-semibold text-indigo-700 mb-3 flex items-center">
              <span className="text-indigo-500"></span> Role Fit Analysis
            </h3>
            <p className="mb-2">
              <strong>{report?.candidate_info.job_title} Fit Score:</strong>{" "}
              {report?.role_fit_analysis?.current_role_fit_score === undefined
                ? report?.current_role_fit_score
                : report?.role_fit_analysis?.current_role_fit_score}
            </p>
            <p className="mb-2">
              {report?.role_fit_analysis?.current_role_fit_insight === undefined
                ? report?.current_role_fit_insight
                : report?.role_fit_analysis?.current_role_fit_insight}
            </p>
            <p className="mb-2 font-bold text-base text-indigo-700">
              Alternative Role Suggestions:
            </p>
            <ul className="list-disc list-inside mt-2 pl-4">
              { }
              {(
                report?.role_fit_analysis?.suggested_roles ??
                report?.suggested_roles
              )?.map((role, idx) => (
                <li key={idx}>
                  <strong>{role.role}</strong> (Score: {role.fit_score}) –{" "}
                  {role.insight}
                </li>
              ))}
            </ul>
          </div>

          {/* Team Fit */}
          {report?.assessment_for === "New Joiner Assessment"
            || report?.user_type === "individual"
            ?
            <> </>
            :
            <>
              {report?.team_fit === undefined ? (
                <></>
              ) : (
                <div className="bg-white border border-indigo-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-all">
                  <h3 className="text-lg font-semibold text-indigo-700 mb-3 flex items-center">
                    <span className="text-indigo-500"></span> Team Fit
                  </h3>
                  <p>
                    <strong>Strengths:</strong>{" "}
                    {report?.team_fit?.strengths?.join(", ")}
                  </p>
                  <p>
                    <strong>Working Style:</strong>{" "}
                    {report?.team_fit?.working_style}
                  </p>
                  <p>
                    <strong>Ideal Environment:</strong>{" "}
                    {report?.team_fit?.ideal_environment}
                  </p>
                  <p>
                    <strong>Risks:</strong> {report?.team_fit?.risks?.join(", ")}
                  </p>
                </div>
              )}

              {/* Manager Tips */}
              {report?.manager_tips === undefined ? (
                <></>
              ) : (
                <div className="bg-white border border-indigo-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-all">
                  <h3 className="text-lg font-semibold text-indigo-700 mb-3 flex items-center gap-2">
                    <span className="text-indigo-500"></span> Manager Tips
                  </h3>
                  <ul className="list-disc list-inside space-y-1 pl-4">
                    {report?.manager_tips?.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          }

          {/* Development Recommendations */}
          {report?.development_recommendations === undefined ? (
            <></>
          ) : (
            <div className="bg-white border border-indigo-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-all">
              <h3 className="text-lg font-semibold text-indigo-700 mb-3 flex items-center gap-2">
                <span className="text-indigo-500"></span> Development
                Recommendations
              </h3>
              <ul className="list-disc list-inside space-y-1 pl-4">
                {report?.development_recommendations?.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Promotion Readiness */}
          {report?.promotion_readiness?.summary === undefined ? (
            <></>
          ) : (
            <div className="bg-white border border-indigo-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-all">
              <h3 className="text-lg font-semibold text-indigo-700 mb-3 flex items-center">
                <span className="text-indigo-500"></span> Promotion Readiness
              </h3>
              <p className="leading-relaxed">
                {report?.promotion_readiness?.summary ===
                  "[Placeholder: Promotion readiness analysis could not be generated.]"
                  ? "-"
                  : report?.promotion_readiness?.summary}
              </p>
            </div>
          )}

          {/* Overall Summary */}
          {report?.summary === undefined ? (
            <></>
          ) : (
            <div className="col-span-1 md:col-span-2 bg-white border border-indigo-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-all">
              <h3 className="text-lg font-semibold text-indigo-700 mb-3 flex items-center">
                <span className="text-indigo-500"></span> Overall Summary
              </h3>
              <p className="leading-relaxed">
                {report?.summary === "Narrative summary could not be generated."
                  ? "-"
                  : report?.summary}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
